import { Component, Input, OnInit } from '@angular/core';
import { CommonModule, DecimalPipe, DatePipe } from '@angular/common';
import { Announcement, AnnouncementType, LookupValue } from '../../../../core/models/announcement.model';

@Component({
  selector: 'app-listing-main-info',
  standalone: true,
  imports: [CommonModule, DecimalPipe, DatePipe],
  template: `
    <div class="info-container" *ngIf="listing">
      <div class="price-section">
        <h1 class="price">{{ listing.price | number:'1.0-0' }} ₼{{ listing.type === announcementType.Rent ? '/ay' : '' }}</h1>
        <div class="price-per-m2" *ngIf="pricePerM2">
          {{ pricePerM2 | number:'1.0-0' }} ₼/m²
        </div>
      </div>

      <div class="location-section">
        <p class="location-text">
          <i class="fas fa-map-marker-alt"></i>
          {{ listing.region || (isLookup(listing.administrativeArea) ? listing.administrativeArea.name : '') }}, {{ listing.address }}
        </p>
        <p class="metro-text" *ngIf="listing.metro || listing.metroId">
          <i class="fas fa-subway"></i>
          {{ isLookup(listing.metro) ? listing.metro.name : 'Yaxınlıqda metro' }}
        </p>
        <p class="created-date">
          {{ listing.createdDate | date:'dd.MM.yyyy HH:mm' }}
        </p>
      </div>

      <div class="specs-grid">
        <div class="spec-item" *ngIf="listing.rooms || listing.roomCount">
          <span class="spec-value">{{ formattedRooms }} otaqlı</span>
        </div>
        <div class="spec-item" *ngIf="listing.area_m2 || listing.areaSquareMeters">
          <span class="spec-value">{{ listing.area_m2 || listing.areaSquareMeters }} m²</span>
        </div>
        <div class="spec-item" *ngIf="listing.floor !== undefined && listing.floor !== null">
          <span class="spec-value">{{ formattedFloor }} mərtəbə</span>
        </div>
        <div class="spec-item" *ngIf="listing.landAreaSot">
          <span class="spec-value">{{ listing.landAreaSot }} sot</span>
        </div>
      </div>

      <div class="attributes-list">
        <div class="attr-item" *ngIf="listing.hasExtract !== undefined || listing.hasDocument !== undefined">
          <span class="attr-label">Çıxarış:</span>
          <span class="attr-value">{{ (listing.hasExtract ?? listing.hasDocument) ? 'Var' : 'Yoxdur' }}</span>
        </div>
        <div class="attr-item" *ngIf="listing.hasMortgage !== undefined || listing.isMortgageAvailable !== undefined">
          <span class="attr-label">İpoteka:</span>
          <span class="attr-value">{{ (listing.hasMortgage ?? listing.isMortgageAvailable) ? 'Var' : 'Yoxdur' }}</span>
        </div>
        <div class="attr-item" *ngIf="listing.repair || listing.renovationStatus">
          <span class="attr-label">Təmir:</span>
          <span class="attr-value">{{ listing.repair || (isLookup(listing.renovationStatus) ? listing.renovationStatus.name : listing.renovationStatus) }}</span>
        </div>
        <div class="attr-item" *ngIf="listing.propertyType">
          <span class="attr-label">Əmlak növü:</span>
          <span class="attr-value">{{ isLookup(listing.propertyType) ? listing.propertyType.name : listing.propertyType }}</span>
        </div>
      </div>

      <div class="badges-row">
        <span class="badge" [class.agent]="listing.ownerType === 'agent'">
          {{ listing.ownerType === 'agent' ? 'Vasitəçi' : 'Mülkiyyətçi' }}
        </span>
        <span class="badge vip" *ngIf="listing.isVip">VIP</span>
      </div>
    </div>
  `,
  styles: [`
    .info-container {
      display: flex;
      flex-direction: column;
      gap: 20px;
    }
    .price {
      font-size: 32px;
      font-weight: 700;
      color: #2d3436;
      margin: 0;
    }
    .price-per-m2 {
      font-size: 16px;
      color: #636e72;
      margin-top: 4px;
    }
    .location-text {
      font-size: 18px;
      color: #2d3436;
      margin-bottom: 4px;
      display: flex;
      align-items: center;
      gap: 8px;
    }
    .location-text i { color: #007bff; }
    .metro-text {
      font-size: 16px;
      color: #00b894;
      margin-bottom: 8px;
      display: flex;
      align-items: center;
      gap: 8px;
      font-weight: 500;
    }
    .created-date {
      font-size: 14px;
      color: #b2bec3;
    }
    .specs-grid {
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
    }
    .spec-item {
      background: #f1f2f6;
      padding: 8px 16px;
      border-radius: 8px;
      font-weight: 600;
      color: #2d3436;
    }
    .attributes-list {
      display: grid;
      grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
      gap: 12px;
      padding-top: 12px;
      border-top: 1px solid #f1f2f6;
    }
    .attr-item {
      display: flex;
      gap: 8px;
    }
    .attr-label { color: #636e72; }
    .attr-value { font-weight: 500; color: #2d3436; }
    .badges-row {
      display: flex;
      gap: 8px;
    }
    .badge {
      padding: 4px 12px;
      border-radius: 6px;
      font-size: 14px;
      font-weight: 600;
      background: var(--primary-color);
      color: #fff;
    }
    .badge.agent { background: #e74c3c; color: #fff; }
    .badge.vip { background: #fff9c4; color: #fbc02d; }
  `]
})
export class ListingMainInfoComponent {
  @Input() listing!: Announcement;
  announcementType = AnnouncementType;

  get pricePerM2(): number | null {
    if (!this.listing || !this.listing.price) return null;
    const area = this.listing.area_m2 || this.listing.areaSquareMeters || (this.listing.landAreaSot ? this.listing.landAreaSot * 100 : 0);
    return area ? Math.round(this.listing.price / area) : null;
  }

  isLookup(val: any): val is LookupValue {
    return !!(val && typeof val === 'object' && 'name' in val);
  }

  get formattedRooms(): string {
    const rooms = this.listing.rooms || this.listing.roomCount;
    if (!rooms) return '';
    return this.isLookup(rooms) ? rooms.name : rooms.toString();
  }

  get formattedFloor(): string {
    const floor = this.listing.floor;
    const totalFloors = this.listing.total_floors || this.listing.totalFloors;

    if (floor === undefined || floor === null) return '';

    const floorStr = this.isLookup(floor) ? floor.name : floor.toString();
    if (!totalFloors) return floorStr;

    const totalStr = this.isLookup(totalFloors) ? totalFloors.name : totalFloors.toString();
    return `${floorStr}/${totalStr}`;
  }
}
